import { z } from 'zod';
/**
 * Periodic todos (TODOS.md §7).
 *
 * A recurring todo stores only its **rule**; there are no generated occurrence
 * rows (the agenda pattern — see AGENDA.md §7). The todo also carries a
 * `scheduled_at`, which is both its reminder time and the anchor the rule steps
 * from. Completing a recurring todo marks it `done` and rolls `scheduled_at`
 * forward to the next occurrence, so it drops out of the active list into its
 * category's collapsed "Klaar" section and reappears — derived here via
 * `isDueReached`, not a stored flip, so it needs no background job — on the day
 * of that next deadline. The existing five-minute reminder sweep still fires for
 * the new instant. This is why the module needs no occurrence table and no
 * server-side expansion.
 *
 * v1 covers exactly what "every day or at certain intervals" asks for:
 *   - `daily`  with `interval` N  → every N days (1 = every day)
 *   - `weekly` with `weekdays`    → specific weekdays (Mon/Wed/Fri, …)
 *
 * This module is independent of the agenda, so it does **not** reuse
 * `agenda/recurrence.ts`; the shapes differ anyway (a todo repeats daily and by
 * weekday-set; the agenda repeats every-N-weeks on one weekday).
 */
/** Weekday numbering matches `Date.getUTCDay()`: 0 = Sunday … 6 = Saturday. */
export const todoRecurrence = z
    .object({
    freq: z.enum(['daily', 'weekly']),
    /** Every N days (daily only). 1 = every day. Ignored for weekly. */
    interval: z.number().int().positive().max(365).default(1),
    /** Which weekdays (weekly only), 0–6. At least one; ignored for daily. */
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
})
    .refine((r) => r.freq !== 'weekly' || r.weekdays.length > 0, {
    message: 'weekly recurrence needs at least one weekday',
});
const DAY_MS = 86_400_000;
const HOUSEHOLD_TZ = 'Europe/Amsterdam';
const ymdFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: HOUSEHOLD_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});
/**
 * The weekday (0–6, Sun–Sat) an instant falls on in the household's timezone,
 * computed deterministically regardless of the runtime's own zone — the same
 * approach the agenda's `localDateKey` uses, kept local so the todo module
 * imports nothing from the agenda.
 */
function householdWeekday(ms) {
    const ymd = ymdFmt.format(new Date(ms)); // "YYYY-MM-DD"
    return new Date(`${ymd}T00:00:00Z`).getUTCDay();
}
/**
 * Has a recurring todo's next-occurrence **day** arrived? True when the
 * household-local date of `scheduledIso` is today or earlier.
 *
 * This is the "reappear on the day of the new deadline" rule (TODOS.md §3):
 * completing a recurring todo marks it done and rolls `scheduled_at` forward, so
 * it drops out of the active list; it comes back only once this returns true for
 * the new instant. It compares whole **days**, not the reminder instant, so a
 * todo due "Wednesday 19:00" reappears at the start of Wednesday — which is how a
 * household reads a deadline. Compared in Europe/Amsterdam so it is deterministic
 * regardless of the runtime's own zone (as `nextDue` is).
 */
export function isDueReached(scheduledIso, nowIso) {
    const s = Date.parse(scheduledIso);
    const n = Date.parse(nowIso);
    if (!Number.isFinite(s) || !Number.isFinite(n))
        return true;
    return ymdFmt.format(new Date(s)) <= ymdFmt.format(new Date(n)); // "YYYY-MM-DD" sorts lexically
}
/**
 * The next occurrence strictly after both `from` (the current `scheduled_at`) and
 * `now`, keeping `from`'s time-of-day. Stepping is in fixed 24-hour spans, so an
 * occurrence crossing a DST boundary can land an hour off its wall-clock time
 * twice a year — the same accepted imprecision the agenda documents; a household
 * reminder does not need better.
 *
 * Anchoring on `from` keeps the cadence: completing a todo a day early still
 * schedules the *next* grid point, and completing one that is a week overdue
 * jumps forward to the next future occurrence rather than replaying the misses.
 */
export function nextDue(rule, fromIso, nowIso) {
    const from = Date.parse(fromIso);
    const now = Date.parse(nowIso);
    if (!Number.isFinite(from))
        return fromIso;
    const floor = Number.isFinite(now) ? now : from;
    if (rule.freq === 'daily') {
        const step = Math.max(1, rule.interval || 1) * DAY_MS;
        let next = from + step;
        if (next <= floor) {
            const jumps = Math.ceil((floor - next) / step);
            next += jumps * step;
            if (next <= floor)
                next += step;
        }
        return new Date(next).toISOString();
    }
    // weekly: scan forward day by day for the next matching weekday. Jump close to
    // `floor` first so a long-overdue todo does not iterate for years, then look at
    // most two weeks ahead — a non-empty weekday set always hits within seven days.
    const set = new Set(rule.weekdays);
    let d = 1;
    if (floor > from + DAY_MS)
        d = Math.floor((floor - from) / DAY_MS);
    for (let i = 0; i < 15; i++, d++) {
        const cand = from + d * DAY_MS;
        if (cand <= floor)
            continue;
        if (set.has(householdWeekday(cand)))
            return new Date(cand).toISOString();
    }
    // Unreachable for a valid rule (weekdays is non-empty); degrade to +1 week.
    return new Date(from + 7 * DAY_MS).toISOString();
}
//# sourceMappingURL=recurrence.js.map